import React, {useContext, useMemo, useRef, useState} from "react";
import {Button, Input, Radio, Table, Tag} from "antd";
import {StarFilled, StarOutlined} from "@ant-design/icons";
import toast from "react-hot-toast";
import TablePage from "../../pages/tablePage/TablePage";
import {AuthContext} from "../../shared/contexts/authContext";
import {getItemName} from "../../shared/utils/basic";
import {internalApi} from "../../common/axios";
import {ShopPackageEntity} from "./ShopPackageEntity";
import {ShopPackageTableEntity} from "./ShopPackageTableEntity";
import {useItemKrTextData, useShopPackage, useShopPackageGroup} from "../probability/useItemData";
import {productType, productTypeList} from "../../shared/utils/enum";
import {useSaveCharacterPackage} from "../gameManager/characterPackage/useCharacterPackage";
import {CharacterPackageEntity} from "../gameManager/characterPackage/CharacterPackageEntity";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";

const subTypeList = [
    {text: '필요 챕터', color: 'magenta'},
    {text: '도달 레벨', color: 'blue'},
    {text: '도달 챕터', color: 'green'},
    {text: '필요 레벨', color: 'purple'},
    {text: '마지막 필요 챕터', color: 'red'},
    {text: '마지막 필요 레벨', color: 'orange'},
]

const StoreTableFav = ({filters}) => {
    const {currentUser} = useContext(AuthContext);
    const [rowData, setRowData] = useState({});
    const [csvData, setCsvData] = useState({});
    const [searchText, setSearchText] = useState("");
    const [showFavOnly, setShowFavOnly] = useState(true);
    const [isChanged, setIsChanged] = useState(0);

    const refreshRef = useRef(null);
    const inputRef = useRef(null);

    const {data: krItemTextData, isLoading: isTextLoading, error: textError} = useItemKrTextData();
    const {data: shopPackage, isLoading: isPackageLoading, error: packageError} = useShopPackage();
    const {data: shopPackageGroup, isLoading: isGroupLoading, error: groupError} = useShopPackageGroup();
    const {mutate: saveCharacterPackage, isPending: isSaveCharacterPackagePending} = useSaveCharacterPackage();

    const handleSave = (record) => {
        for (const key of Object.keys(shopPackageGroup[currentUser.target])) {
            const item = shopPackageGroup[currentUser.target][key];
            if (item?.groupIdx === record.groupIdx) {
                if (!item?.nameText) {
                    console.log(item);
                    toast.error("일부 상품명이 존재 하지 않습니다. 저장을 중단합니다. text_kr.csv 파일을 확인해주세요.");
                    return;
                }
            }
        }
        record.packageName = Object.values(shopPackageGroup[currentUser.target]).find(e => e?.groupIdx === record.groupIdx)?.nameText || '알 수 없음';
        switch (record.productType) {
            case productType.CHARACTER_PACKAGE:
                const characterPackage = new CharacterPackageEntity(record);
                if (characterPackage.isInvalidate()) return;
                saveCharacterPackage(characterPackage.toJson(), {
                    onSuccess: () => {
                        toast.dismiss();
                        toast.success("저장 성공");
                    },
                    onError: (err) => {
                        console.error(err);
                        toast.error("저장 실패");
                    }
                });
                break;
            default:
                const row = new ShopPackageEntity(record);
                if (row.isInvalidate()) return;
                internalApi.post('store_manager/new', {data: row.toJson()}).then((res) => {
                    toast.success(res.data.message);
                }).catch((err) => {
                    console.error(err);
                    toast.error("저장 실패");
                });
        }
    }

    const handleMemoSave = (record, memo) => {
        const row = new ShopPackageEntity({...record, memo});
        if (row.isInvalidate()) return;
        internalApi.post('store_manager/csv/memo', {data: row.toJson()}).then((res) => {
            toast.success(res.data.message);
        }).catch((err) => {
            console.error(err);
            toast.error("메모 저장 실패");
        });
    }

    const handleFavToggle = async (e, record) => {
        e.stopPropagation();
        const newFav = record.isFav ? 0 : 1;
        try {
            await internalApi.post('/store_manager/csv/fav', {index: record.index, isFav: newFav});
            toast.success(newFav ? '즐겨찾기에 추가되었습니다.' : '즐겨찾기가 해제되었습니다.');
            setIsChanged(c => c + 1);
        } catch (e) {
            toast.error('즐겨찾기 변경에 실패했습니다.');
        }
    };

    const getProductTypeColor = (type) => productTypeList[type - 1]?.color || 'default';
    const getProductTypeText = (type) => productTypeList[type - 1]?.text || '알 수 없음';

    const normalize = str => (str || '').replace(/\s+/g, '').toLowerCase();
    const matchedIndexes = useMemo(() => {
        if (!searchText) return [];
        const groupList = Object.values(shopPackageGroup?.[currentUser.target] || {});
        const normSearch = normalize(searchText);
        return groupList
            .filter(g => g.nameText && normalize(g.nameText).includes(normSearch))
            .map((g) => Object.values(shopPackage[currentUser.target]).filter(p => p.groupIdx === g.groupIdx).map(p => p.index));
    }, [searchText, currentUser.target, shopPackage]);

    const columns = [
        {
            title: '즐겨찾기',
            key: 'isFav',
            width: 72,
            render: (_, record) => (
                <Button
                    type="text"
                    icon={record.isFav
                        ? <StarFilled style={{color: '#faad14', fontSize: 18}}/>
                        : <StarOutlined style={{fontSize: 18}}/>
                    }
                    onClick={(e) => handleFavToggle(e, record)}
                />
            ),
        },
        {
            title: '인덱스',
            dataIndex: 'index',
            key: 'index',
            sorter: (a, b) => a.index - b.index,
            filterIcon: (filtered) => (
                <span
                    role="img"
                    aria-label="search"
                    style={{color: filtered ? '#1890ff' : undefined}}
                >
                    🔍
                </span>
            ),
            filterDropdown: ({setSelectedKeys, confirm, clearFilters}) => (
                <div style={{padding: 8}}>
                    <Input
                        placeholder="상품이름(nameText) 검색"
                        ref={inputRef}
                        value={searchText}
                        onChange={e => {
                            setSearchText(e.target.value);
                            setSelectedKeys(e.target.value ? [e.target.value] : []);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') confirm();
                        }}
                        style={{width: 188, marginBottom: 8, display: 'block'}}
                    />
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        size="small"
                        style={{width: 90, marginRight: 8}}
                    >
                        검색
                    </Button>
                    <Button onClick={() => {
                        setSearchText("");
                        clearFilters();
                        confirm();
                    }} size="small" style={{width: 90}}>
                        초기화
                    </Button>
                </div>
            ),
            filterDropdownProps: {
                onOpenChange: (visible) => {
                    if (visible) {
                        setTimeout(() => inputRef.current?.select(), 100);
                    }
                },
            },
            onFilter: (value, record) => {
                if (!searchText) return true;
                const flatIndexes = matchedIndexes.flat();
                return flatIndexes.includes(record.index);
            },
            filteredValue: searchText ? [searchText] : null,
        },
        {
            title: '상품 이름',
            key: 'nameText',
            render: (_, {groupIdx}) => {
                return Object.values(shopPackageGroup[currentUser.target]).find(e => e?.groupIdx === groupIdx)?.nameText || '알 수 없음';
            }
        },
        {
            title: '판매 체크',
            dataIndex: 'openChk',
            key: 'openChk',
            search: true,
        },
        {
            title: '패키지 그룹',
            dataIndex: 'groupIdx',
            key: 'groupIdx',
            sorter: (a, b) => a.groupIdx - b.groupIdx,
            search: true,
        },
        {
            title: '상품 종류',
            dataIndex: 'productType',
            key: 'productType',
            filters: productTypeList,
            onFilter: (value, record) => record.productType === value,
            render: (_, {productType}) => (
                <Tag color={getProductTypeColor(productType) || 'default'}>
                    {getProductTypeText(productType) || '알 수 없음'}
                </Tag>
            ),
        },
        {
            title: '서브 타입',
            dataIndex: 'subType',
            key: 'subType',
            render: (_, {subType}) => (
                <Tag color={subTypeList[subType - 1]?.color || 'default'}>
                    {subTypeList[subType - 1]?.text || '알 수 없음'}
                </Tag>
            ),
        },
        {
            title: '필요 값',
            dataIndex: 'subTypeValue',
            key: 'subTypeValue',
        },
        {
            title: '미션 그룹',
            dataIndex: 'missionGroup',
            key: 'missionGroup',
        },
        {
            title: '재구매',
            dataIndex: 'reBuy',
            key: 'reBuy',
        },
        {
            title: '패키지 별 운영기간(h)',
            dataIndex: 'packageTime',
            key: 'packageTime',
        },
        {
            title: '최소 적용 버전',
            dataIndex: 'minVersion',
            key: 'minVersion',
            render: (_, {index}) => (
                shopPackage[currentUser.target][index]?.openVersion || '테이블을 업로드 해주세요.'
            )
        },
        {
            title: '메모',
            dataIndex: 'memo',
            key: 'memo',
            render: (_, row) => (
                <Input
                    key={`memo-${row.index}`}
                    type="text"
                    defaultValue={row.memo}
                    style={{width: '100%'}}
                    onBlur={(e) => handleMemoSave(row, e.target.value)}
                    onPressEnter={(e) => handleMemoSave(row, e.target.value)}
                />
            )
        },
        {
            title: '액션',
            render: (record) => (
                <Button onClick={() => handleSave(record)}>
                    {'추가하기'}
                </Button>
            )
        }
    ];

    const expandColumns = useMemo(() => ([
        {title: '인덱스', dataIndex: 'index', key: 'index'},
        {title: '상품이름', dataIndex: 'nameText', key: 'nameText'},
        {title: '인앱 인덱스', dataIndex: 'inAppIdx', key: 'inAppIdx'},
        {title: '할인 인앱 인덱스', dataIndex: 'inAppSaleIdx', key: 'inAppSaleIdx'},
        {title: '다이아 가격', dataIndex: 'diaBuyValue', key: 'diaBuyValue'},
        {
            title: '보상 아이템',
            dataIndex: 'rewards',
            key: 'rewards',
            render: (rewards) => rewards.map((reward, idx) => (
                <div key={idx}>
                    {getItemName(krItemTextData[currentUser.target], reward.id, reward.type)} x{reward.quantity}
                </div>
            )),
        },
    ]), [currentUser.target, krItemTextData]);

    const expandedRowRender = (record) => (
        <Table
            columns={expandColumns}
            dataSource={Object.values(shopPackageGroup[currentUser.target])
                .filter((group) => group?.groupIdx === shopPackage[currentUser.target][record.index]?.groupIdx)
                .map((group, index) => ({...group, key: group.groupIdx + index}))}
            pagination={false}
        />
    );

    if (isTextLoading || isPackageLoading || isGroupLoading) return <IsLoading/>;

    const firstError = textError || packageError || groupError;
    if (firstError) return <ErrorPage error={firstError}/>;

    if (isSaveCharacterPackagePending) toast.loading('저장 중...');

    const url = showFavOnly ? '/store_manager/csv/fav?isFav=1' : '/store_manager/csv/fav';

    const tableProps = {
        columns,
        rowData,
        setRowData,
        refreshRef,
        url,
        isChanged,
        tblData: csvData,
        setTblData: setCsvData,
        expandedRowRender,
        entityModel: ShopPackageTableEntity,
        filters,
    };

    return (
        <div>
            <div style={{marginBottom: 16}}>
                <Radio.Group
                    value={showFavOnly}
                    onChange={(e) => setShowFavOnly(e.target.value)}
                    buttonStyle="solid"
                >
                    <Radio.Button value={true}>★ 즐겨찾기만 보기</Radio.Button>
                    <Radio.Button value={false}>전체 보기</Radio.Button>
                </Radio.Group>
            </div>
            <TablePage {...tableProps}/>
        </div>
    );
}

export default StoreTableFav;
